import moment from 'moment';
import timezone from 'moment-timezone';

import { FormatValue } from './types';

export const abbreviateNumber = (
  value: number,
  decimal?: number,
  units: string[] = ['', 'K', 'M', 'B', 'T'],
  unit: string[] = ['', 'K', 'M', 'B', 'T']
): string => {
  const number = Number(value || 0);
  const fix = !decimal || decimal < 0 ? 0 : decimal;
  const b = number.toPrecision(2).split('e');
  const k = b.length === 1 ? 0 : Math.floor(Math.min(+b[1].slice(1), 14) / 3);

  // specified decimals, regardless of whether they're zeros
  const c = k < 1 ? number.toFixed(fix) : (number / 10 ** (k * 3)).toFixed(fix);

  // remove any trailing zeros after decimal point if they exist
  const d = +c < 0 ? Number(c) : Math.abs(+c);

  // convert back to string with fixed decimal places
  const n = d.toFixed(fix);

  const e = n + (d === 1 ? unit[k] : units[k]);
  return e;
};

export const invalidDate = (value?: FormatValue) => {
  if (value === null || value === undefined) {
    return true;
  }
  return !moment(String(value || '')).isValid();
};

export const invalidUnix = (value?: FormatValue) => {
  if (value === null || value === undefined) {
    return true;
  }

  const number = Number(value);

  if (isNaN(number)) {
    return true;
  }

  const min_timestamp = 0; // 1970
  const max_timestamp = 4102444800; // 2100

  const timestamp = convertUnix(number);

  return timestamp < min_timestamp || timestamp > max_timestamp;
};

export const convertUnix = (value?: FormatValue): number => {
  const string = String(value);
  const number = Number(value);
  if (string.length === 13) {
    return Math.floor(number / 1000);
  }
  return number;
};

export const invalidNumber = (value?: FormatValue) => {
  if (value === '') return true;
  const number = typeof value === 'string' ? Number(value) : value;
  return (
    number === null || number === undefined || Number.isNaN(number) || !Number.isFinite(number)
  );
};

export const invalidDuration = (value?: FormatValue) => {
  if (value === 0 || value === '0') return false;
  if (value === '') return true;
  const number = typeof value === 'string' ? Number(value) : value;
  return (
    number === null || number === undefined || Number.isNaN(number) || !Number.isFinite(number)
  );
};

export const createMap = <E extends Record<string, string>>(obj: E) => {
  const values = Object.values(obj) as E[keyof E][];
  const mapping: { [key in E[keyof E]]: keyof E } = {} as any;
  values.forEach((value) => {
    mapping[value] = value as keyof E;
  });
  return mapping;
};

export const getLocalTimeZone = (): string => {
  const timeZoneContry = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone.tz(timeZoneContry).format('z');
};
